import React, { Component, type ReactNode } from 'react'

interface Props {
  children: ReactNode
}

interface State {
  hasError: boolean
}

/**
 * Top-level error boundary. Catches uncaught JS errors anywhere in the tree
 * and shows a friendly recovery screen instead of a blank page.
 * Sentry is already wired via vite.config - it captures the error before
 * this renders, so we don't need to re-report here.
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(): State {
    return { hasError: true }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Sentry already captures this globally; log for local debugging only.
    console.error('[ErrorBoundary]', error, info.componentStack)
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 max-w-sm w-full text-center">
            <div className="text-5xl mb-4">😕</div>
            <h1 className="text-xl font-semibold text-gray-900 mb-2">Algo correu mal</h1>
            <p className="text-sm text-gray-500 mb-6 leading-relaxed">
              Ocorreu um erro inesperado. A equipa foi notificada automaticamente.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="w-full bg-indigo-600 text-white py-3 rounded-xl font-semibold hover:bg-indigo-700 transition-colors"
            >
              Recarregar app
            </button>
            <button
              onClick={() => { window.location.href = '/subjects' }}
              className="w-full mt-2 text-sm text-gray-400 hover:text-gray-600 py-2"
            >
              Voltar ao início
            </button>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
